import { Loading, Panel, Tooltip } from "@appkit4/react-components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { getUltParent } from "../../components/CompanyPanel";
import toast from "../../components/DismissibleToast";
import { BackButton, NextButton } from "../../components/ProgressNav";
import {
  Button,
  CalendarPicker,
  Input,
  InputNumber,
} from "../../components/ReadonlyAwareInputs";
import CustomTableHeader from "../../components/tableHeader/tableHeader";
import { AuthContext } from "../../services/AuthProvider";
import {
  getGroupData,
  getGroupID,
  getGroupName,
  getPageTooltips,
  getPeriodIsReadonly,
  getPoaIndex,
  setGroupData,
  setPoaIndex,
  setSelectedPeriodData,
  setViewMode,
} from "../../services/GroupContext";
import sqlService, {
  deleteWBSLogs,
  updateWBSLogs,
} from "../../services/sqldatabase/sqldatabase.service";
import {
  getDefaultPeriodEnd,
  getMaxPeriodEnd,
  getOriginatingPoaMinStartDateJS,
  processDate,
  processDateJS,
} from "../../utils/dateProcessor";
import { formatNegativeNumber } from "../../utils/formatters";
import { getDefaultPeriod } from "../new-group/NewGroup";
import AdminOpenPeriodsModal from "./AdminOpenPeriodsModal";
import "./groupPoa.scss";
import {
  datesMatchGroupConfig,
  defaultReturnStatus,
  formatReturnStatus,
  getDatesErrorMessage,
  getDefaultEnd,
  getNextDay,
  getOldSavedPoAs,
  getSelectDisabledFlags,
  headerSettings,
  isInvalidDate,
  normalizePeriodsForUI,
  updateCreatedCompanyIdsList,
} from "./GroupPoAUtils";
import OpenPeriodModal from "./OpenPeriodModal";

dayjs.extend(utc);

const GroupDetailsPanel = memo(function GroupDetailsPanel({
  groupName,
  ultimateParent,
}) {
  return (
    <Panel style={{ paddingTop: "20px" }}>
      <div className="row">
        <label className="col-sm-2">Group Name</label>
        <div className="col-sm-10">
          <span className="bold-text">{groupName}</span>
        </div>
      </div>
      <div className="row">
        <label className="col-sm-2">Ultimate Parent</label>
        <div className="col-sm-10">
          <span className="bold-text">{ultimateParent}</span>
        </div>
      </div>
    </Panel>
  );
});

const GroupPOA = () => {
  const auth = useContext(AuthContext);
  const isAdmin = auth.isAdmin();
  const isReadOnly = auth?.isReadOnlyUser();

  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasErrors, setHasErrors] = useState([]);
  const errorsExist = new Set([...hasErrors]).has(true);

  const [tableData, setTableData] = useState([]);
  const [minPOAStart, setMinPOAStart] = useState([
    processDate(getOriginatingPoaMinStartDateJS()),
  ]);
  const [maxPOAEnd, setMaxPOAEnd] = useState([]);

  const [isPoaStartDateValid, setIsPoaStartDateValid] = useState([]);
  const [isPoaEndDateValid, setIsPoaEndDateValid] = useState([]);
  const [arePoaDatesValid, setArePoaDatesValid] = useState([]);
  const [selectButtonsDisabledFlags, setSelectButtonsDisabledFlags] = useState(
    [],
  );

  const [showOpenPeriodModal, setShowOpenPeriodModal] = useState(false);
  const [showAdminOpenPeriodsModal, setShowAdminOpenPeriodsModal] =
    useState(false);
  const [openPeriodIndex, setOpenPeriodIndex] = useState(-1);

  const tooltips = useMemo(() => getPageTooltips("groupPoA"), []);
  const [newPoAIds, setNewPoAIds] = useState(new Set());
  const changedPoaIdsRef = useRef({});
  const deletedPoaIdsRef = useRef([]);
  const originalPoaDates = useRef(new Map());
  const navigate = useNavigate();

  const getWbsLogsUpdatePromise = () => {
    const wbsLogsUpdatesExist =
      Object.keys(changedPoaIdsRef.current)?.length > 0;
    const wbsLogDeletionsExist = deletedPoaIdsRef.current?.length > 0;

    const groupID = getGroupID();
    const promises = [
      wbsLogsUpdatesExist
        ? updateWBSLogs(groupID, changedPoaIdsRef.current)
        : null,
      wbsLogDeletionsExist
        ? deleteWBSLogs(groupID, deletedPoaIdsRef.current)
        : null,
    ];

    return promises;
  };

  const submit = async (customOnSuccess = () => {}) => {
    if (errorsExist) {
      toast.error("There are validation errors on the table!");
      return;
    }

    setIsPosting(true);

    const groupData = getGroupData();
    const newJson = updateCreatedCompanyIdsList({
      ...groupData,
      periods_of_account: tableData,
    });

    const poaIndex = getPoaIndex();
    let PoAPosition;
    if (poaIndex === 0) {
      PoAPosition = "start";
    } else if (poaIndex === tableData.length - 1) {
      PoAPosition = "last";
    } else {
      PoAPosition = "inBetween";
    }

    const promises = [
      sqlService?.postResponse(newJson),
      ...getWbsLogsUpdatePromise(),
    ];
    const promise = Promise.all(promises);

    const onSuccess = () => {
      setGroupData(newJson);
      setSelectedPeriodData({ PoAPosition }, true);
      customOnSuccess();
      setIsPosting(false);
      changedPoaIdsRef.current = {};
      deletedPoaIdsRef.current = [];
      setNewPoAIds(new Set());
      originalPoaDates.current = getOldSavedPoAs(tableData);
    };
    const onError = () => {
      setIsPosting(false);
    };

    if (isReadOnly) {
      customOnSuccess();
      return;
    } else {
      return toast.save(promise, onSuccess, onError);
    }
  };

  const applyDatesValidations = (periods_of_account, index = null) => {
    const tempMaxPOAEnd = [...maxPOAEnd];
    const tempMinPOAStart = [...minPOAStart];

    const setDateRanges = (index) => {
      const row = periods_of_account[index];
      if (!row) {
        return;
      }

      const { period_start } = row ?? {};

      if (period_start) {
        tempMaxPOAEnd[index] = getMaxPeriodEnd(period_start);
      }

      const previousPeriodEnd = periods_of_account[index - 1]?.period_end;
      if (index > 0 && previousPeriodEnd) {
        tempMinPOAStart[index] = getNextDay(previousPeriodEnd);
      }
    };

    if (index !== null) {
      [index, index + 1].forEach((index) => setDateRanges(index));
    } else {
      periods_of_account.forEach((_, index) => setDateRanges(index));
    }

    setMinPOAStart(tempMinPOAStart);
    setMaxPOAEnd(tempMaxPOAEnd);
  };

  useEffect(() => {
    setViewMode(false);
    const savedData = getGroupData();
    if (!savedData) {
      return;
    }

    let { periods_of_account } = savedData;
    if (!periods_of_account) {
      const { model_first_period_start, model_first_period_end } = savedData;
      // If PoA data is empty, populate it
      periods_of_account = [
        getDefaultPeriod(model_first_period_start, model_first_period_end),
      ];
    }

    periods_of_account = normalizePeriodsForUI(periods_of_account);

    const initialValidationFlags = new Array(periods_of_account.length).fill(
      true,
    );
    setIsPoaStartDateValid([...initialValidationFlags]);
    setIsPoaEndDateValid([...initialValidationFlags]);
    setHasErrors([...initialValidationFlags]);

    originalPoaDates.current = getOldSavedPoAs(periods_of_account);
    applyDatesValidations(periods_of_account);
    isTableValid(periods_of_account);

    setTableData(periods_of_account);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    setSelectButtonsDisabledFlags(getSelectDisabledFlags(tableData));
  }, [tableData]);

  useEffect(() => {
    const newArePoaDatesValid = isPoaStartDateValid?.map(
      (isValid, index) => isValid && isPoaEndDateValid[index],
    );

    setArePoaDatesValid(newArePoaDatesValid);
  }, [isPoaStartDateValid, isPoaEndDateValid, tableData]);

  const validateRowDates = (row) => {
    const { period_start, period_end } = row ?? {};

    if (isInvalidDate(period_start) || isInvalidDate(period_end)) {
      return true;
    }

    if (processDateJS(period_end)?.isBefore(period_start)) {
      return true;
    }

    if (getMaxPeriodEnd(period_start)?.isBefore(period_end)) {
      return true;
    }

    return false;
  };

  // entire table is validated only on load
  const isTableValid = (periods_of_account) => {
    const newHasErrors = periods_of_account.map((row) => validateRowDates(row));
    setHasErrors(newHasErrors);
  };

  const isUnChangedValue = (poa, key, value) => {
    const metric = key.split("_").slice(1).join("_");
    const oldValue = changedPoaIdsRef.current[poa.id]?.[`old_${metric}`];

    return new Date(oldValue).getTime() === new Date(value).getTime();
  };

  // track changes for current PoA new end & next PoA new start
  const trackPoAChanges = (poa, key, value) => {
    if (!poa?.id) return;

    // there is an id, but the id is not in the original dates loaded
    if (!originalPoaDates.current.has(poa.id)) return;

    if (isUnChangedValue(poa, key, value)) {
      deletePoAIdFromTrackedChanges(poa.id);
      return;
    }

    changedPoaIdsRef.current[poa.id] = {
      old_period_end: originalPoaDates.current.get(poa.id).period_end,
      new_period_end: poa.period_end,
      old_period_start: originalPoaDates.current.get(poa.id).period_start,
      new_period_start: poa.period_start,
      [key]: new Date(processDateJS(value)?.format("YYYY-MM-DD")).toISOString(),
    };
  };

  const deletePoAIdFromTrackedChanges = (poa_id) => {
    if (!poa_id) return;

    delete changedPoaIdsRef.current[poa_id];
  };

  const checkDateErrors = (rowsInput, index, name, value) => {
    const rowToValidate = { ...rowsInput[index], [name]: value };
    const rowToValidateBefore = rowsInput[index - 1];
    const rowToValidateAfter = rowsInput[index + 1];

    setHasErrors((prev) => {
      const next = [...prev];
      if (rowToValidateBefore) {
        next[index - 1] = validateRowDates(rowToValidateBefore);
      }

      next[index] = validateRowDates(rowToValidate);

      if (rowToValidateAfter) {
        next[index + 1] = validateRowDates(rowToValidateAfter);
      }

      return next;
    });
  };

  const tableChange = (index, value, name) => {
    const rowsInput = [...tableData];
    const row = rowsInput[index];

    if (name === "period_start" || name === "period_end") {
      value = processDate(value);

      // Auto-populate periods if possible
      const { period_start, period_end } = row ?? {};
      if (period_start && !period_end) {
        // default end is 1 year after start
        rowsInput[index]["period_end"] = getDefaultEnd(period_start);
      }

      const prevPeriodEnd = rowsInput?.[index - 1]?.period_end;
      if (index > 0 && prevPeriodEnd && !period_start) {
        // default start is one day after previous end
        rowsInput[index]["period_start"] = getNextDay(prevPeriodEnd);
      }

      if (name === "period_end") {
        if (value) {
          trackPoAChanges(row, "new_period_end", value);
        } else {
          deletePoAIdFromTrackedChanges(row?.id);
        }

        // if period end is changed before last row, it will affect the start of the next period
        if (index < tableData?.length - 1) {
          const newNextStartDate = getNextDay(value);
          const nextPeriod = rowsInput[index + 1];

          if (value) {
            trackPoAChanges(nextPeriod, "new_period_start", newNextStartDate);
            nextPeriod["period_start"] = newNextStartDate;
          } else {
            deletePoAIdFromTrackedChanges(nextPeriod?.id);
          }
        }
      }

      rowsInput[index][name] = value;

      checkDateErrors(rowsInput, index, name, value);
      applyDatesValidations(rowsInput, index);
    }

    rowsInput[index][name] = value;
    setTableData(rowsInput);
  };

  const addTableRows = () => {
    // assume the table data is sorted
    const lastPoA = tableData[tableData.length - 1];
    const new_period_start = processDateJS(lastPoA?.period_end)?.add(1, "day");
    const new_period_end = getDefaultPeriodEnd(new_period_start);

    const poa_id = uuidv4();
    setTableData([
      ...tableData,
      {
        id: poa_id,
        period_start: new_period_start?.toDate(),
        period_end: new_period_end?.toDate(),
        return_status: { ...defaultReturnStatus },
        ratio_type: "Not Selected",
        disallowance_reactivation: "",
        group_excess_debt_cap: "",
        unused_interest_allowance: "",
        unused_allowance: "",
        hasPoaSpecificWbsCode: true,
      },
    ]);
    setNewPoAIds((prev) => new Set([...prev, poa_id]));

    setIsPoaStartDateValid((prevValidators) => [...prevValidators, true]);
    setIsPoaEndDateValid((prevValidators) => [...prevValidators, true]);
    setHasErrors((prevErrors) => [...prevErrors, false]);

    const {
      lastCompletedPoACalcs,
      return_status: { isFinalised },
    } = lastPoA ?? {};

    const isPrevPeriodDisabled =
      lastCompletedPoACalcs !== "editcfd" && !isFinalised;

    setSelectButtonsDisabledFlags([
      ...selectButtonsDisabledFlags,
      isPrevPeriodDisabled,
    ]);
  };

  const deleteTableRows = (index) => {
    const rows = [...tableData];

    const poa_id = rows[index]?.id;
    if (originalPoaDates.current.has(poa_id)) {
      deletedPoaIdsRef.current.push(poa_id);
      delete changedPoaIdsRef.current[poa_id];
    }

    const deleteFunction = (_, idx) => idx !== index;

    setTableData((rows) => rows?.filter(deleteFunction));
    setIsPoaStartDateValid((prevState) => prevState?.filter(deleteFunction));
    setIsPoaEndDateValid((prevState) => prevState?.filter(deleteFunction));
    setHasErrors((prevState) => prevState?.filter(deleteFunction));

    const disabledFlags = [...selectButtonsDisabledFlags];
    disabledFlags.splice(index, 1);
    setSelectButtonsDisabledFlags(disabledFlags);
  };

  const getAlignmentStyles = (isDateValid, padding = false) => {
    return {
      verticalAlign: isDateValid ? "middle" : "top",
      ...(padding && { paddingTop: isDateValid ? "" : "0.3rem" }),
    };
  };

  if (isLoading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  }

  return (
    <div className="ap-container">
      <OpenPeriodModal
        visible={showOpenPeriodModal}
        setVisible={setShowOpenPeriodModal}
        periodIndexToOpen={openPeriodIndex}
        tableData={tableData}
        updateTableData={setTableData}
      />
      {isAdmin && (
        <AdminOpenPeriodsModal
          visible={showAdminOpenPeriodsModal}
          setVisible={setShowAdminOpenPeriodsModal}
          tableData={tableData}
          updateTableData={setTableData}
        />
      )}
      <GroupDetailsPanel
        groupName={getGroupName()}
        ultimateParent={getUltParent()}
      />
      <Panel className="mt-4">
        <div style={{ paddingTop: "20px" }}>
          {isAdmin && (
            <div className="d-flex justify-content-end mb-3">
              <Button
                className="button-min-width"
                onClick={() => setShowAdminOpenPeriodsModal(true)}
              >
                Open Periods as Admin
              </Button>
            </div>
          )}
          <table className="table" style={{ width: "100%" }}>
            <thead>
              <tr>
                {headerSettings.map(
                  ({ title, tooltipText, required }, index) => (
                    <CustomTableHeader
                      key={index}
                      title={title}
                      required={required}
                      tooltipText={tooltips[tooltipText]}
                    />
                  ),
                )}
                <th className="col" key={7}></th>
                <th className="col" key={8}></th>
                <th key={9}>
                  <Button
                    add
                    className="btn btn-outline-success"
                    disabled={errorsExist || isPosting}
                    onClick={addTableRows}
                  >
                    <span className="Appkit4-icon icon-plus-outline"></span>
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData?.map((data, index) => {
                const isPoaReadOnly = getPeriodIsReadonly(data);
                const { isFinalised, isSubmitted } = data?.return_status ?? {};
                const isFirstRow = index === 0;
                const isLastRow = index === tableData?.length - 1;
                const canDelete = !isFirstRow && isLastRow && !isSubmitted;

                const {
                  period_start,
                  period_end,
                  ratio_type,
                  status_reviewcalc2_completed,
                  group_disallowance,
                  group_reactivations,
                  group_excess_debt_cap,
                  group_bf_interest_allowance,
                  unused_generated,
                } = data ?? {};

                const firstRowDatesMismatch =
                  isFirstRow &&
                  !datesMatchGroupConfig(period_start, period_end);

                const inputFieldStyle = getAlignmentStyles(
                  arePoaDatesValid[index],
                );
                const buttonStyle = getAlignmentStyles(
                  arePoaDatesValid[index],
                  true,
                );

                return (
                  <tr key={index}>
                    <td key={1} style={inputFieldStyle}>
                      <CalendarPicker
                        format="DD/MM/YYYY"
                        placeholder="dd/mm/yyyy"
                        className="col"
                        fieldWidth={150}
                        value={period_start}
                        minDate={minPOAStart[index]}
                        disabled={!isFirstRow || isPoaReadOnly}
                        useCustomValidation
                        error={
                          !isPoaStartDateValid[index] || firstRowDatesMismatch
                        }
                        customErrorNode={getDatesErrorMessage(index, "start")}
                        onChange={(val, isValid) => {
                          tableChange(index, val, "period_start");
                          setIsPoaStartDateValid((prevState) => {
                            const newState = [...prevState];
                            newState[index] = isValid;
                            return newState;
                          });
                        }}
                      />
                    </td>
                    <td key={2} style={inputFieldStyle}>
                      <CalendarPicker
                        key={maxPOAEnd[index]}
                        format="DD/MM/YYYY"
                        placeholder="dd/mm/yyyy"
                        className="col"
                        fieldWidth={150}
                        value={period_end}
                        minDate={period_start}
                        maxDate={maxPOAEnd[index]}
                        disabled={isPoaReadOnly || isPosting}
                        useCustomValidation
                        error={
                          !isPoaEndDateValid[index] ||
                          firstRowDatesMismatch ||
                          hasErrors[index]
                        }
                        customErrorNode={getDatesErrorMessage(index, "end")}
                        onChange={(val, isValid) => {
                          tableChange(index, val, "period_end");
                          setIsPoaEndDateValid((prevState) => {
                            const newState = [...prevState];
                            newState[index] = isValid;
                            return newState;
                          });
                        }}
                      />
                    </td>
                    <td key={3} style={inputFieldStyle}>
                      <Input readonly value={formatReturnStatus(data)} />
                    </td>
                    <td key={4} style={inputFieldStyle}>
                      <Input readonly value={ratio_type} />
                    </td>
                    <td key={5} style={inputFieldStyle}>
                      <InputNumber
                        readonly
                        value={
                          status_reviewcalc2_completed
                            ? group_disallowance
                              ? Math.round(group_disallowance)
                              : formatNegativeNumber(group_reactivations)
                            : ""
                        }
                        format=","
                      />
                    </td>
                    <td key={6} style={inputFieldStyle}>
                      <InputNumber
                        readonly
                        min={0}
                        value={
                          status_reviewcalc2_completed
                            ? Math.round(group_excess_debt_cap)
                            : ""
                        }
                        format=","
                      />
                    </td>
                    <td key={7} style={inputFieldStyle}>
                      <InputNumber
                        readonly
                        min={0}
                        value={
                          status_reviewcalc2_completed
                            ? group_bf_interest_allowance
                              ? formatNegativeNumber(
                                  group_bf_interest_allowance,
                                )
                              : Math.round(unused_generated)
                            : ""
                        }
                        format=","
                      />
                    </td>
                    <td key={8} style={buttonStyle}>
                      <NextButton
                        className="btn button-min-width"
                        disabled={errorsExist}
                        loading={isPosting}
                        preNavigation={async () => {
                          const setPoAData = () => {
                            setPoaIndex(index);
                            setViewMode(!isFinalised);
                          };

                          if (newPoAIds.has(data?.id)) {
                            await submit(setPoAData);
                          } else {
                            setPoAData();
                          }
                        }}
                      >
                        View
                      </NextButton>
                    </td>
                    <td key={9} style={buttonStyle}>
                      {isPoaReadOnly ? (
                        <Button
                          className="button-min-width"
                          disabled={errorsExist}
                          loading={isPosting}
                          onClick={() => {
                            setOpenPeriodIndex(index);
                            setShowOpenPeriodModal(true);
                          }}
                        >
                          Open
                        </Button>
                      ) : (
                        <Tooltip
                          disabled={!selectButtonsDisabledFlags[index]}
                          position="top"
                          style={{ maxWidth: "20rem" }}
                          content={() => (
                            <p className="no-margin">
                              This period cannot be edited as the calculations
                              in the prior period have not been completed.
                              Navigate past the Carried Forward Disallowances
                              page in that period to complete the calculations.
                            </p>
                          )}
                        >
                          <div>
                            <NextButton
                              className="btn button-min-width"
                              disabled={
                                isReadOnly ||
                                errorsExist ||
                                selectButtonsDisabledFlags[index]
                              }
                              loading={isPosting}
                              preNavigation={async () => {
                                const setPoAData = () => {
                                  setPoaIndex(index);
                                  setViewMode(false);
                                };

                                await submit(setPoAData);
                              }}
                            >
                              Select
                            </NextButton>
                          </div>
                        </Tooltip>
                      )}
                    </td>
                    <td key={10} style={buttonStyle}>
                      {!isPoaReadOnly && canDelete && (
                        <Button
                          className="btn btn-outline-danger"
                          disabled={isPosting || errorsExist}
                          onClick={() => deleteTableRows(index)}
                        >
                          <span className="Appkit4-icon icon-close-outline" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <BackButton disabled={isPosting} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupPOA;
